import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import cache, { CacheKeys, CacheTTL } from '../utils/cache';
import { formatDateEG, formatNumericDateEG } from '../utils/dateUtils';
import Navbar from '../student/Navbar';
import AssistantSidebar from './AssistantSidebar';
import Loader from '../components/Loader';
import withRoleGuard from '../hoc/withRoleGuard';
import { motion } from 'framer-motion';
import { FaUsers, FaUserAlt, FaNotesMedical, FaCalendarAlt, FaPlus, FaDownload, FaUserPlus, FaFilter, FaEdit, FaTrash, FaChevronLeft, FaChevronRight, FaClone } from 'react-icons/fa';
import AddAppointmentModal from './AddAppointmentModal';
import * as XLSX from 'xlsx';

// Website color palette - Updated to match the system design
const websiteColorPalette = {
  primary: '#0077B6',      // Main blue
  secondary: '#20B2AA',    // Teal
  background: '#FFFFFF',   // White
  text: '#333333',         // Dark gray
  accent: '#28A745',       // Green
  lightBlue: '#E3F2FD',    // Light blue background
  darkBlue: '#1565C0',     // Darker blue for hover states
  gray: {
    50: '#F9FAFB',
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827'
  }
};

// Unified chronic diseases options for both Add and Edit forms
const CHRONIC_DISEASE_OPTIONS = [
  'Diabetes',
  'Hypertension',
  'Heart Disease',
  'Asthma',
  'Arthritis',
  'Cancer',
  'Kidney Disease',
  'Liver Disease',
  'Thyroid Disease',
  'Depression',
  'Anxiety',
  'Other'
];


// Security: Input validation and sanitization
const sanitizeInput = (input) => {
  if (typeof input !== 'string') return '';
  return input.trim().replace(/[<>\"']/g, '');
};

const validateNationalId = (id) => {
  if (!id || typeof id !== 'string') return false;
  const sanitized = sanitizeInput(id);
  return /^[A-Z0-9-]+$/.test(sanitized) && sanitized.length <= 20;
};

const validatePhoneNumber = (phone) => {
  if (!phone || typeof phone !== 'string') return false;
  const sanitized = sanitizeInput(phone);
  return /^[\d\s\-+()]+$/.test(sanitized) && sanitized.length <= 20;
};

const validateName = (name) => {
  if (!name || typeof name !== 'string') return false;
  const sanitized = sanitizeInput(name);
  return /^[a-zA-Z\s\u0600-\u06FF]+$/.test(sanitized) && sanitized.length <= 100;
};

// Helper to generate a TEMP-<random 8 digit number> National ID
function generateTempNationalId() {
  return `TEMP-${Math.floor(10000000 + Math.random() * 90000000)}`;
}

// Is this National ID already used by ANY patient?
// The per-student check reports "available" for unassigned patients, so it can
// never catch a repeated TEMP id - and almost every patient is unassigned.
// Reusing one made several unrelated people share a National ID, which in turn
// made edits land on the wrong record.
async function nationalIdTakenByAnyone(apiUrl, token, nationalId) {
  try {
    const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
    const res = await fetch(
      `${apiUrl}/api/admin/patients/check-nationalid?nationalId=${encodeURIComponent(nationalId)}&scope=any`,
      config
    );
    if (res.status !== 200) return false;
    const data = await res.json();
    return Boolean(data.exists);
  } catch (err) {
    // Never block patient creation on this check.
    return false;
  }
}

// Produce a TEMP National ID that no existing patient is using.
async function generateUniqueTempNationalId(apiUrl, token) {
  for (let attempt = 0; attempt < 8; attempt++) {
    const candidate = generateTempNationalId();
    // eslint-disable-next-line no-await-in-loop
    if (!(await nationalIdTakenByAnyone(apiUrl, token, candidate))) {
      return candidate;
    }
  }
  return null;
}

// Lightweight check using dedicated backend endpoint instead of fetching all patients
async function checkNationalIdExistsForStudent(apiUrl, token, nationalId, drId) {
  try {
    if (!drId || drId === 'unassigned' || drId === 'N/A' || drId === '') return false;

    const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
    const res = await fetch(
      `${apiUrl}/api/admin/patients/check-nationalid?nationalId=${encodeURIComponent(nationalId)}&drId=${encodeURIComponent(drId)}`,
      config
    );
    if (res.status === 200) {
      const data = await res.json();
      return data.exists;
    }
    return true;
  } catch (e) {
    return true;
  }
}

// Per-form id sent with a create so a repeated submission is not saved twice.
function newClientRequestId() {
  if (typeof window !== 'undefined' && window.crypto && typeof window.crypto.randomUUID === 'function') {
    return window.crypto.randomUUID();
  }
  return `req-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

// National IDs can be missing on older records; the URL segment still needs a
// value, and the document id in `patientId` is what really identifies the row.
const patientPath = (patient) => encodeURIComponent(String(patient?.nationalId ?? '').trim() || 'none');

const DUPLICATE_PAGE_SIZE = 25;

const DUPLICATE_REASON_LABELS = {
  nationalId: 'Same National ID',
  phone: 'Same phone',
  name: 'Same name',
  serial: 'Same serial'
};

// Drop a deleted record from the duplicate groups; a group left with one
// record is no longer a duplicate, and the oldest remaining one becomes the
// original.
const removeFromDuplicateGroups = (groups, patientId) => groups
  .map(group => {
    if (!group.patients.some(p => p.id === patientId)) return group;
    const remaining = group.patients.filter(p => p.id !== patientId);
    return {
      ...group,
      count: remaining.length,
      patients: remaining.map((p, i) => ({ ...p, isOriginal: i === 0, duplicateOf: i === 0 ? null : remaining[0].id }))
    };
  })
  .filter(group => group.patients.length > 1);

const SERIAL_PREFIX = 'SN';

const generateRandomSerialNumber = () => {
  const randomPart = Math.floor(100000 + Math.random() * 900000);
  return `${SERIAL_PREFIX}-${randomPart}`;
};

async function checkSerialNumberExists(apiUrl, token, serialNumber, excludeNationalId) {
  if (!serialNumber || !apiUrl) return false;

  const config = {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    params: {
      serialNumber: serialNumber.trim()
    }
  };

  if (excludeNationalId) {
    config.params.excludeNationalId = excludeNationalId;
  }

  const response = await axios.get(`${apiUrl}/api/patients/check-serial`, config);
  return Boolean(response.data?.exists);
}

const AssistantPatients = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [patients, setPatients] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [patientDetails, setPatientDetails] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Students pagination state
  const [, setStudentsPagination] = useState({
    page: 1,
    limit: 100, // Load more students at once for dropdown
    total: 0,
    totalPages: 0
  });
  const [formData, setFormData] = useState({
    nationalId: '',
    fullName: '',
    phoneNumber: '',
    gender: '',
    age: '',
    address: '',
    occupation: '',
    drId: '',
    registrationDate: new Date().toISOString().split('T')[0], // Add registration date with today's date
    notes: '', // Add notes field
    serialNumber: '', // Add serial number field
    medicalInfo: {
      chronicDiseases: [],
      recentSurgicalProcedures: '',
      currentMedications: '',
      chiefComplaint: '',
      otherChronicDisease: ''
    }
  });
  const [studentSearch, setStudentSearch] = useState('');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigningPatient, setAssigningPatient] = useState(null);
  const [assignSearch, setAssignSearch] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignError, setAssignError] = useState('');
  const { user, token } = useAuth();
  const apiBaseUrl = process.env.REACT_APP_API_URL;

  const [showStudentAssignPopup, setShowStudentAssignPopup] = useState(false);
  const [procedureRequests, setProcedureRequests] = useState([]);
  const [showProcedureFilter, setShowProcedureFilter] = useState(false);
  const [selectedProcedureType, setSelectedProcedureType] = useState('all');
  const [showAddAppointmentModal, setShowAddAppointmentModal] = useState(false);
  const [appointmentPatient, setAppointmentPatient] = useState(null);
  const [appointmentDefaultStudentId, setAppointmentDefaultStudentId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deletingPatient, setDeletingPatient] = useState(null);
  const [showDeleteSuccessModal, setShowDeleteSuccessModal] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const deleteSubmittingRef = useRef(false);
  // Add-patient submission guard. The form runs several network checks before
  // it POSTs, so a second click used to start a second create and save the
  // patient twice. The ref blocks re-entry synchronously; the request id lets
  // the backend recognise a repeated submission of the same form.
  const [addSubmitting, setAddSubmitting] = useState(false);
  const addSubmittingRef = useRef(false);
  const addRequestIdRef = useRef(null);
  // Show duplicates
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [duplicateSummary, setDuplicateSummary] = useState(null);
  const [duplicatesLoading, setDuplicatesLoading] = useState(false);
  const [duplicatesError, setDuplicatesError] = useState('');
  const [duplicateReasonFilter, setDuplicateReasonFilter] = useState('all');
  const [duplicateSearch, setDuplicateSearch] = useState('');
  const [duplicateVisibleCount, setDuplicateVisibleCount] = useState(DUPLICATE_PAGE_SIZE);

  const [editFormData, setEditFormData] = useState({
    nationalId: '',
    fullName: '',
    phoneNumber: '',
    gender: '',
    age: '',
    address: '',
    occupation: '',
    drId: '',
    registrationDate: new Date().toISOString().split('T')[0],
    notes: '',
    serialNumber: '',
    medicalInfo: {
      chronicDiseases: [],
      recentSurgicalProcedures: '',
      currentMedications: '',
      chiefComplaint: '',
      otherChronicDisease: ''
    }
  });

  const [serialChecking, setSerialChecking] = useState(false);
  const [serialValidationMessage, setSerialValidationMessage] = useState('');
  const [serialConflict, setSerialConflict] = useState(false);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [studentFilter, setStudentFilter] = useState('all');
  const [sortBy, setSortBy] = useState('registrationDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchLoading, setSearchLoading] = useState(false);

  // Use patients directly since they're already paginated from server, but ensure local fallback search supports serial numbers
  const paginatedPatients = useMemo(() => {
    if (!Array.isArray(patients)) return [];
    const trimmedSearch = searchTerm.trim().toLowerCase();

    if (!trimmedSearch) return patients;

    return patients.filter(patient => {
      if (!patient) return false;
      const name = (patient.fullName || '').toLowerCase();
      const nationalId = (patient.nationalId || '').toLowerCase();
      const phone = (patient.phoneNumber || '').toLowerCase();
      const serial = (patient.serialNumber || '').toLowerCase();

      return (
        name.includes(trimmedSearch) ||
        nationalId.includes(trimmedSearch) ||
        phone.includes(trimmedSearch) ||
        serial.includes(trimmedSearch)
      );
    });
  }, [patients, searchTerm]);

  // Update pagination info when server response changes
  useEffect(() => {
    if (pagination.total === 0 && patients.length > 0) {
      setPagination(prev => ({
        ...prev,
        total: patients.length,
        totalPages: Math.ceil(patients.length / prev.limit),
        hasNextPage: prev.page < Math.ceil(patients.length / prev.limit),
        hasPrevPage: prev.page > 1
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only sync when patients length and pagination state
  }, [patients, pagination.page, pagination.limit]);

  // Excel Download functionality - always export all patients across pages
  const downloadCSV = async () => {
    try {
      setError('');
      setRefreshing(true);

      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

      // paginate through all patients
      let allPatients = [];
      let page = 1;
      const limit = 200;
      let hasNext = true;

      while (hasNext) {
        const params = new URLSearchParams({ page: String(page), limit: String(limit) });
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/patients?${params.toString()}`, config);
        const data = res.data;
        const batch = Array.isArray(data) ? data : (Array.isArray(data.patients) ? data.patients : []);
        allPatients = allPatients.concat(batch);
        if (Array.isArray(data)) {
          hasNext = false;
        } else {
          hasNext = Boolean(data.pagination?.hasNextPage);
        }
        page += 1;
        if (batch.length === 0) break;
      }

      if (allPatients.length === 0) {
        setError('No patients to download');
        return;
      }

      // Helper function to clean values
      const clean = (val) => {
        if (val === null || val === undefined) return '';
        if (typeof val === 'string') return val.replace(/[\r\n]+/g, ' ').trim();
        if (Array.isArray(val)) return val.join('; ');
        return String(val);
      };

      // Helper function to format dates
      // Day-first (Egyptian) date for the export, e.g. "21/12/2026".
      const formatDate = (dateValue) => {
        if (!dateValue) return '';
        const formatted = formatNumericDateEG(dateValue);
        return formatted === 'Invalid Date' ? '' : formatted;
      };

      // Prepare Excel data with all patient fields
      const excelData = allPatients.map(patient => {
        const assignedStudent = Array.isArray(students) ? students.find(s => s.studentId === patient.drId) : null;
        const chronic = Array.isArray(patient.medicalInfo?.chronicDiseases) ? patient.medicalInfo.chronicDiseases : [];
        const hasOther = chronic.includes('Other');
        const otherNote = patient.medicalInfo?.otherChronicDisease || '';
        const chronicBase = chronic.filter(d => d !== 'Other');
        const chronicFormatted = hasOther && otherNote 
          ? (chronicBase.length > 0 ? `${chronicBase.join('; ')}; Other: ${otherNote}` : `Other: ${otherNote}`)
          : chronicBase.length > 0 ? chronicBase.join('; ') : (hasOther ? 'Other' : '');

        return {
          // Basic Information
          'Full Name': clean(patient.fullName),
          'National ID': clean(patient.nationalId),
          'Serial Number': clean(patient.serialNumber || ''),
          'Phone Number': clean(patient.phoneNumber),
          'Gender': clean(patient.gender),
          'Age': patient.age || '',
          'Address': clean(patient.address),
          'Occupation': clean(patient.occupation),
          
          // Assignment Information
          'Assigned Student Name': clean(assignedStudent?.name || ''),
          'Student ID (drId)': clean(patient.drId || ''),
          
          // Dates
          'Registration Date': formatDate(patient.registrationDate),
          'Created At': formatDate(patient.createdAt),
          'Updated At': formatDate(patient.updatedAt),
          
          // Notes
          'Notes': clean(patient.notes || ''),
          
          // Medical Information
          'Chief Complaint': clean(patient.medicalInfo?.chiefComplaint || ''),
          'Current Medications': clean(patient.medicalInfo?.currentMedications || ''),
          'Recent Surgical Procedures': clean(patient.medicalInfo?.recentSurgicalProcedures || ''),
          'Chronic Diseases': chronicFormatted,
          'Other Chronic Disease Note': clean(otherNote),
          
          // Additional Information
          'X-Rays Count': Array.isArray(patient.xrays) ? patient.xrays.length : 0,
          'Gallery Images Count': Array.isArray(patient.galleryImages) ? patient.galleryImages.length : 0,
          'Appointments Count': Array.isArray(patient.appointments) ? patient.appointments.length : 0,
          'Teeth Charts Count': Array.isArray(patient.teethCharts) ? patient.teethCharts.length : 0,
          'Treatment Sheets Count': Array.isArray(patient.treatmentSheets) ? patient.treatmentSheets.length : 0
        };
      });

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Set column widths for better readability
      const colWidths = [
        { wch: 25 }, // Full Name
        { wch: 15 }, // National ID
        { wch: 15 }, // Serial Number
        { wch: 15 }, // Phone Number
        { wch: 10 }, // Gender
        { wch: 5 },  // Age
        { wch: 30 }, // Address
        { wch: 20 }, // Occupation
        { wch: 25 }, // Assigned Student Name
        { wch: 15 }, // Student ID
        { wch: 18 }, // Registration Date
        { wch: 18 }, // Created At
        { wch: 18 }, // Updated At
        { wch: 30 }, // Notes
        { wch: 30 }, // Chief Complaint
        { wch: 30 }, // Current Medications
        { wch: 30 }, // Recent Surgical Procedures
        { wch: 40 }, // Chronic Diseases
        { wch: 30 }, // Other Chronic Disease Note
        { wch: 15 }, // X-Rays Count
        { wch: 18 }, // Gallery Images Count
        { wch: 18 }, // Appointments Count
        { wch: 18 }, // Teeth Charts Count
        { wch: 20 }  // Treatment Sheets Count
      ];
      ws['!cols'] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Patients');

      // Generate Excel file and download
      const fileName = `patients_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      setSuccess(`Downloaded ${allPatients.length} patients successfully!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Excel download error:', err);
      setError('Failed to download Excel file');
    } finally {
      setRefreshing(false);
    }
  };

  // When National ID field loses focus and is empty, generate a TEMP National ID
  const handleNationalIdBlur = () => {
    if (!formData.nationalId || formData.nationalId.trim() === '') {
      const existingIds = patients.map(p => p.nationalId);
      const tempId = generateTempNationalId(existingIds);
      setFormData(prev => ({ ...prev, nationalId: tempId }));
    }
  };

  // Animation variants
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  const studentsLoadedRef = useRef(false);
  const procedureRequestsLoadedRef = useRef(false);

  // Fetch students only once (or when forced) - they rarely change
  const fetchStudentsOnce = useCallback(async (force = false) => {
    if (!user || !token) return;
    if (studentsLoadedRef.current && !force) return;

    const config = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const studentsData = await cache.getOrSet(
        CacheKeys.STUDENTS(1, 100),
        async () => {
          const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/students`, config);
          return res.data;
        },
        CacheTTL.LONG
      );

      if (studentsData && studentsData.students) {
        const studentsArray = studentsData.students;
        if (Array.isArray(studentsArray)) {
          setStudents(studentsArray);
          setStudentsPagination(studentsData.pagination);
        }
      } else if (Array.isArray(studentsData)) {
        setStudents(studentsData);
        setStudentsPagination({ page: 1, limit: studentsData.length, total: studentsData.length, totalPages: 1 });
      }
      studentsLoadedRef.current = true;
    } catch (err) {
      console.error('Failed to fetch students:', err);
    }
  }, [user, token]);

  // Fetch procedure requests only once (or when forced)
  const fetchProcedureRequestsOnce = useCallback(async (force = false) => {
    if (!user || !token) return;
    if (procedureRequestsLoadedRef.current && !force) return;

    const config = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const data = await cache.getOrSet(
        CacheKeys.PROCEDURE_REQUESTS(),
        async () => {
          const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/procedure-requests`, config);
          return res.data;
        },
        CacheTTL.MEDIUM
      );
      setProcedureRequests(Array.isArray(data) ? data : []);
      procedureRequestsLoadedRef.current = true;
    } catch (err) {
      console.error('Failed to fetch procedure requests:', err);
    }
  }, [user, token]);

  // Fetch patients only (with caching) - the only thing that changes with pagination/search/filter
  const fetchPatients = useCallback(async (page = 1, limit = 20, search = '', studentFilterValue = 'all', sortByValue = 'registrationDate', sortOrderValue = 'desc') => {
    if (!user || !token) {
      setError('Please log in to view data.');
      setLoading(false);
      return;
    }

    try {
      setRefreshing(true);
      setError('');

      const config = { headers: { Authorization: `Bearer ${token}` } };

      const cacheKey = CacheKeys.PATIENTS(page, limit, search, studentFilterValue, sortByValue, sortOrderValue);
      const patientsData = await cache.getOrSet(
        cacheKey,
        async () => {
          const queryParams = new URLSearchParams({
            page: page.toString(),
            limit: limit.toString(),
            ...(search && { search }),
            ...(studentFilterValue !== 'all' && { studentFilter: studentFilterValue }),
            ...(sortByValue && { sortBy: sortByValue }),
            ...(sortOrderValue && { sortOrder: sortOrderValue })
          });
          const res = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/admin/patients?${queryParams.toString()}`,
            config
          );
          return res.data;
        },
        CacheTTL.MEDIUM
      );

      if (patientsData && patientsData.patients && Array.isArray(patientsData.patients)) {
        setPatients(patientsData.patients);
        setPagination({
          page: patientsData.pagination?.page || page,
          limit: patientsData.pagination?.limit || limit,
          total: patientsData.pagination?.total || patientsData.patients.length,
          totalPages: patientsData.pagination?.totalPages || Math.ceil((patientsData.pagination?.total || patientsData.patients.length) / limit),
          hasNextPage: patientsData.pagination?.hasNextPage || false,
          hasPrevPage: patientsData.pagination?.hasPrevPage || false
        });
      } else if (Array.isArray(patientsData)) {
        setPatients(patientsData);
        setPagination({ page: 1, limit: patientsData.length, total: patientsData.length, totalPages: 1, hasNextPage: false, hasPrevPage: false });
      } else {
        setPatients([]);
        setPagination({ page: 1, limit: 0, total: 0, totalPages: 0, hasNextPage: false, hasPrevPage: false });
      }

      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch data');
      setLoading(false);
    } finally {
      setRefreshing(false);
    }
  }, [user, token]);

  // Invalidate patient cache (call after mutations) and re-fetch current page
  const invalidateAndRefetchPatients = useCallback(() => {
    const stats = cache.getStats();
    stats.keys.forEach(key => {
      if (
        key.startsWith('patients_') ||
        key.startsWith('patient_details_') ||
        key.startsWith('patient_appointments_') ||
        key.startsWith('patient_public_') ||
        // Analytics is derived from the same patient records
        key.startsWith('analytics_') ||
        key.startsWith('patient_entries_')
      ) {
        cache.delete(key);
      }
    });
    fetchPatients(pagination.page, pagination.limit, searchTerm, studentFilter, sortBy, sortOrder);
  }, [fetchPatients, pagination.page, pagination.limit, searchTerm, studentFilter, sortBy, sortOrder]);

  // Combined fetch for initial load
  const fetchData = useCallback(async (page = 1, limit = 20, search = '', studentFilterValue = 'all', sortByValue = 'registrationDate', sortOrderValue = 'desc') => {
    fetchStudentsOnce();
    fetchProcedureRequestsOnce();
    await fetchPatients(page, limit, search, studentFilterValue, sortByValue, sortOrderValue);
  }, [fetchStudentsOnce, fetchProcedureRequestsOnce, fetchPatients]);

  // Function to create initial appointment for new patients
  const createInitialAppointment = async (patient) => {
    // Always create appointment, even for unassigned patients

    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      // Use the registration date from the patient, fallback to today if not available
      let appointmentDate = patient.registrationDate;
      if (!appointmentDate) {
        // If no registration date, use today's date
        appointmentDate = new Date().toISOString().split('T')[0];
        // No registration date found for patient, using today
      }
      
      // Get current time rounded down to nearest 30 minutes
      const now = new Date();
      const hours = now.getHours().toString().padStart(2, '0');
      const minutes = now.getMinutes() >= 30 ? '30' : '00';
      const currentTime = `${hours}:${minutes}`;
      
      // Create appointment data
      const appointmentData = {
        date: appointmentDate,
        time: currentTime, // Use current hour
        type: 'Initial Consultation',
        notes: 'Initial appointment created automatically upon patient registration',
        patient: patient.nationalId,
        doctor: (patient.drId && patient.drId !== 'unassigned' && patient.drId !== 'N/A') ? patient.drId : null,
        doctorModel: (patient.drId && patient.drId !== 'unassigned' && patient.drId !== 'N/A') ? 'Student' : null,
        status: 'completed',
        chiefComplaint: patient.medicalInfo?.chiefComplaint || 'Initial consultation'
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/appointments`,
        appointmentData,
        config
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };





  // Function to handle page changes - only fetches patients (not students/procedureRequests)
  const handlePageChange = useCallback((newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    fetchPatients(newPage, pagination.limit, searchTerm, studentFilter, sortBy, sortOrder);
  }, [fetchPatients, pagination.limit, searchTerm, studentFilter, sortBy, sortOrder]);

  // Function to handle limit changes
  const handleLimitChange = useCallback((newLimit) => {
    setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
    fetchPatients(1, newLimit, searchTerm, studentFilter, sortBy, sortOrder);
  }, [fetchPatients, searchTerm, studentFilter, sortBy, sortOrder]);

  // Ensure patients is always an array
  useEffect(() => {
    if (!Array.isArray(patients)) {
      setPatients([]);
    }
  }, [patients]);

  const searchEffectFirstRun = useRef(true);
  const sortEffectFirstRun = useRef(true);

  // Initial data fetch (patients + students + procedure requests)
  useEffect(() => {
    fetchData(pagination.page, pagination.limit, searchTerm, studentFilter, sortBy, sortOrder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced refetch when search term changes only
  useEffect(() => {
    if (searchEffectFirstRun.current) {
      searchEffectFirstRun.current = false;
      return;
    }
    if (searchTerm) setSearchLoading(true);
    const timeoutId = setTimeout(() => {
      setSearchLoading(false);
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchPatients(1, pagination.limit, searchTerm, studentFilter, sortBy, sortOrder);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, fetchPatients, pagination.limit, studentFilter, sortBy, sortOrder]);

  // Immediate refetch when sort or student filter changes (newest-to-oldest, etc.)
  useEffect(() => {
    if (sortEffectFirstRun.current) {
      sortEffectFirstRun.current = false;
      return;
    }
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchPatients(1, pagination.limit, searchTerm, studentFilter, sortBy, sortOrder);
  }, [studentFilter, sortBy, sortOrder, fetchPatients, pagination.limit, searchTerm]);

  // Listen for patient assignment notifications via storage event (no polling)
  useEffect(() => {
    const checkPatientAssignment = () => {
      const assignedData = localStorage.getItem('patientAssigned');
      if (assignedData) {
        try {
          const data = JSON.parse(assignedData);
          const timeDiff = Date.now() - data.timestamp;
          
          if (timeDiff < 10000) {
            setSuccess(`Patient ${data.patientName} was successfully assigned to ${data.studentName}!`);
            invalidateAndRefetchPatients();
            localStorage.removeItem('patientAssigned');
            setTimeout(() => setSuccess(''), 5000);
          }
        } catch (error) {
          // Silently handle parsing errors
        }
      }
    };

    checkPatientAssignment();

    const handleStorageChange = (e) => {
      if (e.key === 'patientAssigned') checkPatientAssignment();
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [invalidateAndRefetchPatients]);

  // Fetch detailed patient info when a patient is selected (with caching)
  useEffect(() => {
    const fetchPatientDetailsOptimized = async () => {
      if (selectedPerson && !selectedPerson.nationalId) {
        // Nothing to look up by; show the record as listed.
        setPatientDetails(selectedPerson);
        return;
      }
      if (selectedPerson && selectedPerson.nationalId) {
        try {
          const config = { headers: { Authorization: `Bearer ${token}` } };
          const cacheKey = CacheKeys.PATIENT_DETAILS(selectedPerson.nationalId);
          const nid = encodeURIComponent(String(selectedPerson.nationalId));

          const details = await cache.getOrSet(
            cacheKey,
            async () => {
              const [patientResponse, appointmentsResponse] = await Promise.all([
                axios.get(`${process.env.REACT_APP_API_URL}/api/patients/public/${nid}`, config),
                axios.get(`${process.env.REACT_APP_API_URL}/api/appointments/patient/${nid}`, config).catch(() => ({ data: [] }))
              ]);
              return {
                ...patientResponse.data,
                appointments: appointmentsResponse.data || []
              };
            },
            CacheTTL.MEDIUM
          );

          // The lookup is by national ID, so with duplicates it can return a
          // different record. Never show another record's details.
          setPatientDetails(
            details?.id && selectedPerson.id && details.id !== selectedPerson.id
              ? { ...selectedPerson, appointments: details.appointments || [] }
              : details
          );
        } catch (err) {
          setPatientDetails(selectedPerson);
        }
      }
    };

    fetchPatientDetailsOptimized();
  }, [selectedPerson, token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'serialNumber') {
      setSerialConflict(false);
      setSerialValidationMessage('');
    }
    if (name.startsWith('medical-')) {
      const medicalField = name.replace('medical-', '');
      setFormData(prev => ({
        ...prev,
        medicalInfo: { ...prev.medicalInfo, [medicalField]: value }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleChronicDiseaseChange = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => {
      const updatedDiseases = checked
        ? [...prev.medicalInfo.chronicDiseases, value]
        : prev.medicalInfo.chronicDiseases.filter(disease => disease !== value);

      return {
        ...prev,
        medicalInfo: {
          ...prev.medicalInfo,
          chronicDiseases: updatedDiseases,
          ...(value === 'Other' && !checked ? { otherChronicDisease: '' } : {})
        }
      };
    });
  };
  const runSerialValidation = useCallback(async (serialValue) => {
    const candidate = serialValue?.trim();

    if (!candidate) {
      setSerialConflict(false);
      setSerialValidationMessage('');
      return false;
    }

    if (!apiBaseUrl) {
      setSerialConflict(true);
      setSerialValidationMessage('API URL not configured. Cannot validate serial number.');
      return true;
    }

    setSerialChecking(true);
    try {
      const exists = await checkSerialNumberExists(apiBaseUrl, token, candidate);
      setSerialConflict(exists);
      setSerialValidationMessage(
        exists ? 'Serial number already exists. Please choose another one.' : 'Serial number is available.'
      );
      return exists;
    } catch (validationError) {
      console.error('Serial validation failed:', validationError);
      setSerialConflict(true);
      setSerialValidationMessage('Unable to verify serial number right now. Please try again.');
      return true;
    } finally {
      setSerialChecking(false);
    }
  }, [apiBaseUrl, token]);

  const handleSerialBlur = useCallback(() => {
    runSerialValidation(formData.serialNumber);
  }, [formData.serialNumber, runSerialValidation]);

  const handleGenerateSerialNumber = useCallback(async () => {
    if (!apiBaseUrl) {
      setSerialConflict(true);
      setSerialValidationMessage('API URL not configured. Cannot generate serial number.');
      return;
    }

    setSerialChecking(true);
    setSerialConflict(false);
    setSerialValidationMessage('Generating serial number...');

    try {
      let attempts = 0;
      let generatedSerial = '';
      let exists = true;

      while (attempts < 5 && exists) {
        generatedSerial = generateRandomSerialNumber();
        exists = await checkSerialNumberExists(apiBaseUrl, token, generatedSerial);
        attempts++;
      }

      if (exists) {
        setSerialConflict(true);
        setSerialValidationMessage('Unable to generate a unique serial number. Please try again.');
        return;
      }

      setFormData(prev => ({ ...prev, serialNumber: generatedSerial }));
      setSerialConflict(false);
      setSerialValidationMessage('Random serial number generated.');
    } catch (error) {
      console.error('Failed to generate serial number:', error);
      setSerialConflict(true);
      setSerialValidationMessage('Failed to generate serial number. Please try again.');
    } finally {
      setSerialChecking(false);
    }
  }, [apiBaseUrl, token]);

  const handleAddPatient = () => {
    setFormData({
      nationalId: '',
      fullName: '',
      phoneNumber: '',
      gender: '',
      age: '',
      address: '',
      occupation: '',
      drId: '',
      registrationDate: new Date().toISOString().split('T')[0], // Add registration date with today's date
      notes: '', // Add notes field
      serialNumber: '', // Add serial number field
      medicalInfo: {
        chronicDiseases: [],
        recentSurgicalProcedures: '',
        currentMedications: '',
        chiefComplaint: ''
      }
    });
    setError(''); // Clear any previous errors
    setSuccess(''); // Clear any previous success messages
    setSerialConflict(false);
    setSerialValidationMessage('');
    setSerialChecking(false);
    addRequestIdRef.current = newClientRequestId();
    setShowAddModal(true);
  };

  const handleSubmitPatient = async (e) => {
    e.preventDefault();
    if (addSubmittingRef.current) return;

    // Security: Validate and sanitize all inputs
    if (formData.nationalId && !validateNationalId(formData.nationalId)) {
      setError('Invalid National ID format. Only letters, numbers, and hyphens are allowed.');
      return;
    }
    
    if (!validateName(formData.fullName)) {
      setError('Invalid name format. Only letters and spaces are allowed.');
      return;
    }
    
    if (formData.phoneNumber && !validatePhoneNumber(formData.phoneNumber)) {
      setError('Invalid phone number format.');
      return;
    }
    
    // Validate required fields - only name is required
    if (!formData.fullName || formData.fullName.trim() === '') {
      setError('Patient name is required');
      return;
    }

    addSubmittingRef.current = true;
    setAddSubmitting(true);
    if (!addRequestIdRef.current) addRequestIdRef.current = newClientRequestId();

    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      let nationalId = formData.nationalId && formData.nationalId.trim() !== ''
        ? formData.nationalId.trim()
        : null;
      // A TEMP id filled in when the field lost focus was never checked for
      // uniqueness - treat it like an empty field and generate a checked one.
      if (nationalId && /^TEMP-/i.test(nationalId)
        && await nationalIdTakenByAnyone(process.env.REACT_APP_API_URL, token, nationalId)) {
        nationalId = null;
      }
      // If nationalId is empty, generate a TEMP-<random> value that is unique
      // across every patient (not just this student's).
      if (!nationalId) {
        const nationalIdToUse = await generateUniqueTempNationalId(process.env.REACT_APP_API_URL, token);
        if (!nationalIdToUse) {
          setError('Could not generate a unique temporary National ID. Please try again.');
          return;
        }
        setFormData(prev => ({ ...prev, nationalId: nationalIdToUse }));
        nationalId = nationalIdToUse;
      } else {
        // If user entered a nationalId, check with backend if it exists for the same student
        const exists = await checkNationalIdExistsForStudent(process.env.REACT_APP_API_URL, token, nationalId, formData.drId);
        if (exists) {
          setError('A patient with this National ID is already assigned to this student. Please use a different National ID.');
          return;
        }
      }

      const trimmedSerialNumber = formData.serialNumber?.trim();
      if (trimmedSerialNumber) {
        const serialExists = await runSerialValidation(trimmedSerialNumber);
        if (serialExists) {
          alert('Serial number already exists. Please change it or generate a new one.');
          return;
        }
      }
      const patientData = {
        clientRequestId: addRequestIdRef.current,
        nationalId: String(nationalId),
        fullName: formData.fullName,
        phoneNumber: formData.phoneNumber || '000-000-0000',
        gender: formData.gender || 'other',
        age: formData.age ? parseInt(formData.age, 10) : 18,
        address: formData.address || '',
        occupation: formData.occupation || '',
        drId: formData.drId && formData.drId.trim() !== '' ? formData.drId : 'unassigned',
        registrationDate: formData.registrationDate || new Date().toISOString().split('T')[0],
        notes: formData.notes || '', // Add notes field
        serialNumber: trimmedSerialNumber || '', // Add serial number field
        medicalInfo: {
          chronicDiseases: formData.medicalInfo.chronicDiseases || [],
          recentSurgicalProcedures: formData.medicalInfo.recentSurgicalProcedures || '',
          currentMedications: formData.medicalInfo.currentMedications || '',
          chiefComplaint: formData.medicalInfo.chiefComplaint || '',
          otherChronicDisease: formData.medicalInfo.otherChronicDisease || ''
        }
      };

      // Creating patient with validated data

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/patients`,
        patientData,
        config
      );

      console.log('Patient creation response:', response.data);

      if (response.data && response.data.patient) {
        // Find the assigned student to add student information
        const assignedStudent = students.find(s => s.studentId === response.data.patient.drId);
        const newPatient = {
          ...response.data.patient,
          studentName: assignedStudent?.name || (response.data.patient.drId === 'unassigned' ? 'Unassigned' : 'Unknown Student')
        };

        setPatients(prevPatients => [...prevPatients.filter(p => p.id !== newPatient.id), newPatient]);
        addRequestIdRef.current = null;
        setShowAddModal(false);
        setFormData({
          nationalId: '',
          fullName: '',
          phoneNumber: '',
          gender: '',
          age: '',
          address: '',
          occupation: '',
          drId: '',
          registrationDate: new Date().toISOString().split('T')[0], // Add registration date with today's date
          notes: '', // Add notes field
          serialNumber: '', // Add serial number field
          medicalInfo: {
            chronicDiseases: [],
            recentSurgicalProcedures: '',
            currentMedications: '',
            chiefComplaint: '',
            otherChronicDisease: ''
          }
        });
        setError(''); // Clear any previous errors
        setSuccess('Patient added successfully!');

        // Automatically create appointment for the new patient - once. A
        // repeated submission returns the patient saved the first time, which
        // already has its initial appointment.
        try {
          if (response.data.duplicatePrevented) {
            setSuccess('Patient was already saved - no duplicate was created.');
          } else {
            await createInitialAppointment(response.data.patient);
            if (response.data.patient.drId && response.data.patient.drId !== 'unassigned' && response.data.patient.drId !== 'N/A') {
              setSuccess('Patient added successfully! Initial appointment created and assigned to student.');
            } else {
              setSuccess('Patient added successfully! Initial appointment created (unassigned).');
            }
          }
        } catch (appointmentError) {
          console.error('Failed to create initial appointment:', appointmentError);
          setSuccess('Patient added successfully! (Note: Initial appointment creation failed)');
        }

        // Invalidate cache and refresh patient list
        invalidateAndRefetchPatients();

        setTimeout(() => {
          setSuccess('');
        }, 3000);
      }
    } catch (err) {
      console.error('Error creating patient:', err);
      console.error('Error response:', err.response?.data);
      const errorMessage = err.response?.data?.message || 'Failed to create patient';
      const validationErrors = err.response?.data?.errors || [];

      // Handle specific validation errors
      if (err.response?.status === 400 && validationErrors.length > 0) {
        setError(`Validation error: ${validationErrors.join(', ')}`);
      } else if (errorMessage.includes('already exists')) {
        setError('A patient with this National ID already exists for this student. Please use a different National ID.');
      } else {
        setError(errorMessage);
      }
    } finally {
      addSubmittingRef.current = false;
      setAddSubmitting(false);
    }
  };

  // Assignment logic
  const openAssignModal = (patient) => {
    setAssigningPatient(patient);
    setAssignSearch('');
    setAssignError('');
    setShowAssignModal(true);
  };

  const handleEditPatient = (patient) => {
    setError('');
    setEditingPatient(patient);

    // Normalize registrationDate to YYYY-MM-DD for the date input
    let regDate = new Date().toISOString().split('T')[0];
    if (patient.registrationDate) {
      if (typeof patient.registrationDate === 'string') {
        const parsed = new Date(patient.registrationDate);
        if (!isNaN(parsed.getTime())) {
          regDate = parsed.toISOString().split('T')[0];
        }
      } else if (patient.registrationDate._seconds) {
        regDate = new Date(patient.registrationDate._seconds * 1000).toISOString().split('T')[0];
      } else if (patient.registrationDate instanceof Date) {
        regDate = patient.registrationDate.toISOString().split('T')[0];
      }
    }

    setEditFormData({
      nationalId: patient.nationalId != null ? String(patient.nationalId) : '',
      fullName: patient.fullName || '',
      phoneNumber: patient.phoneNumber || '',
      gender: patient.gender || '',
      age: patient.age ? patient.age.toString() : '',
      address: patient.address || '',
      occupation: patient.occupation || '',
      drId: patient.drId || '',
      registrationDate: regDate,
      notes: patient.notes || '',
      serialNumber: patient.serialNumber || '',
      medicalInfo: {
        chronicDiseases: patient.medicalInfo?.chronicDiseases || [],
        recentSurgicalProcedures: patient.medicalInfo?.recentSurgicalProcedures || '',
        currentMedications: patient.medicalInfo?.currentMedications || '',
        chiefComplaint: patient.medicalInfo?.chiefComplaint || '',
        otherChronicDisease: patient.medicalInfo?.otherChronicDisease || ''
      }
    });
    setShowEditModal(true);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('medicalInfo.')) {
      const field = name.split('.')[1];
      setEditFormData(prev => ({
        ...prev,
        medicalInfo: {
          ...prev.medicalInfo,
          [field]: value
        }
      }));
    } else {
      setEditFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleEditChronicDiseaseChange = (e) => {
    const { value, checked } = e.target;
    setEditFormData(prev => ({
      ...prev,
      medicalInfo: {
        ...prev.medicalInfo,
        chronicDiseases: checked
          ? [...prev.medicalInfo.chronicDiseases, value]
          : prev.medicalInfo.chronicDiseases.filter(disease => disease !== value),
        ...(value === 'Other' && !checked ? { otherChronicDisease: '' } : {})
      }
    }));
  };

  // Patients are now filtered and sorted server-side, so we just use them directly

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    // Records are identified by document id; older ones may have no national ID.
    if (!editingPatient?.id || editSubmitting) return;

    // Some records store the national ID as a number, which has no .trim().
    const newNationalId = String(editFormData.nationalId ?? '').trim();
    const originalNationalId = String(editingPatient.nationalId ?? '').trim();
    if (!newNationalId) {
      setError('National ID cannot be empty.');
      return;
    }
    // Only a changed ID is format-checked, so a record with a legacy ID (e.g.
    // lowercase letters) can still have its other fields edited.
    if (newNationalId !== originalNationalId && !validateNationalId(newNationalId.toUpperCase())) {
      setError('Invalid National ID format. Only letters, numbers, and hyphens are allowed.');
      return;
    }
    if (!String(editFormData.fullName || '').trim()) {
      setError('Patient name is required.');
      return;
    }

    setEditSubmitting(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const medicalInfo = editFormData.medicalInfo || {};
      const patientData = {
        // The same national ID can exist on several records (one per student).
        // Send the exact document id so the edit lands on the row being edited
        // instead of an arbitrary duplicate.
        patientId: editingPatient.id,
        currentDrId: editingPatient.drId,
        nationalId: newNationalId,
        // Coerce to the types the backend validates: older records hold
        // numbers in text fields, capitalised genders ("Male") or ages like
        // "25 years", and any of those made the whole update get rejected.
        fullName: String(editFormData.fullName || '').trim(),
        phoneNumber: String(editFormData.phoneNumber ?? ''),
        gender: ['male', 'female', 'other'].includes(String(editFormData.gender || '').toLowerCase())
          ? String(editFormData.gender).toLowerCase()
          : 'other',
        age: Number.isFinite(parseInt(editFormData.age, 10)) ? parseInt(editFormData.age, 10) : 18,
        address: String(editFormData.address ?? ''),
        occupation: String(editFormData.occupation ?? ''),
        drId: String(editFormData.drId || 'unassigned'),
        notes: String(editFormData.notes ?? ''),
        serialNumber: String(editFormData.serialNumber ?? '').trim(),
        registrationDate: editFormData.registrationDate || new Date().toISOString().split('T')[0],
        medicalInfo: {
          // Left out when it is not a list (legacy data) so the stored value
          // is kept rather than wiped.
          ...(Array.isArray(medicalInfo.chronicDiseases) && { chronicDiseases: medicalInfo.chronicDiseases.map(String) }),
          recentSurgicalProcedures: String(medicalInfo.recentSurgicalProcedures ?? ''),
          currentMedications: String(medicalInfo.currentMedications ?? ''),
          chiefComplaint: String(medicalInfo.chiefComplaint ?? ''),
          otherChronicDisease: String(medicalInfo.otherChronicDisease ?? '')
        }
      };

      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/patients/${patientPath(editingPatient)}`,
        patientData,
        config
      );

      const updated = response.data?.patient || response.data;
      if (updated) {
        // Match by document id - duplicates share a national ID.
        setPatients(prevPatients =>
          prevPatients.map(p =>
            p.id === editingPatient.id
              ? { ...updated, studentName: p.studentName }
              : p
          )
        );
        if (showDuplicatesModal) fetchDuplicates();
        // Invalidate patient details cache for both old and new national IDs
        cache.delete(CacheKeys.PATIENT_DETAILS(editingPatient.nationalId));
        if (newNationalId !== editingPatient.nationalId) {
          cache.delete(CacheKeys.PATIENT_DETAILS(newNationalId));
        }
        setShowEditModal(false);
        setEditingPatient(null);
        setEditFormData({
          nationalId: '',
          fullName: '',
          phoneNumber: '',
          gender: '',
          age: '',
          address: '',
          occupation: '',
          drId: '',
          registrationDate: new Date().toISOString().split('T')[0],
          notes: '',
          serialNumber: '',
          medicalInfo: {
            chronicDiseases: [],
            recentSurgicalProcedures: '',
            currentMedications: '',
            chiefComplaint: '',
            otherChronicDisease: ''
          }
        });
        setError('');
        setSuccess('Patient updated successfully!');
        invalidateAndRefetchPatients();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      console.error('Error updating patient:', err);
      setError(err.response?.data?.message || 'Failed to update patient');
    } finally {
      setEditSubmitting(false);
    }
  };

  // Delete patient functions
  const handleDeletePatient = (patient) => {
    setDeletingPatient(patient);
    setShowDeleteConfirmModal(true);
  };

  const confirmDeletePatient = async () => {
    // The ref blocks a second click that lands before React re-renders the
    // disabled button.
    if (!deletingPatient || deleteSubmittingRef.current) return;
    const target = deletingPatient;

    deleteSubmittingRef.current = true;
    setDeleteSubmitting(true);
    try {
      // `patientId` names the exact record. Without it the backend deleted
      // whichever record it found first with this national ID - often a
      // different duplicate - so the clicked row never disappeared.
      const config = {
        headers: { Authorization: `Bearer ${token}` },
        params: { patientId: target.id, currentDrId: target.drId }
      };

      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/patients/${patientPath(target)}`,
        config
      );

      setShowDeleteConfirmModal(false);
      setDeletingPatient(null);
      setShowDeleteSuccessModal(true);

      // Optimistic removal from local state + cache invalidation
      setPatients(prev => prev.filter(p => p.id !== target.id));
      setDuplicateGroups(prev => removeFromDuplicateGroups(prev, target.id));
      if (showDuplicatesModal) fetchDuplicates();
      if (selectedPerson?.id === target.id) {
        setSelectedPerson(null);
        setPatientDetails(null);
      }
      invalidateAndRefetchPatients();

      setTimeout(() => {
        setShowDeleteSuccessModal(false);
      }, 3000);
    } catch (err) {
      console.error('Error deleting patient:', err);
      const message = err.response?.data?.message || 'Failed to delete patient';
      setError(message);
      if (showDuplicatesModal) setDuplicatesError(message);
      setShowDeleteConfirmModal(false);
      setDeletingPatient(null);
    } finally {
      deleteSubmittingRef.current = false;
      setDeleteSubmitting(false);
    }
  };

  const cancelDeletePatient = () => {
    if (deleteSubmitting) return;
    setShowDeleteConfirmModal(false);
    setDeletingPatient(null);
  };

  // ---- Show duplicates ------------------------------------------------------

  const fetchDuplicates = useCallback(async () => {
    if (!token) return;
    setDuplicatesLoading(true);
    setDuplicatesError('');
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/patients/duplicates`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDuplicateGroups(Array.isArray(res.data?.groups) ? res.data.groups : []);
      setDuplicateSummary(res.data?.summary || null);
    } catch (err) {
      setDuplicatesError(err.response?.data?.message || 'Failed to load duplicate patients');
    } finally {
      setDuplicatesLoading(false);
    }
  }, [token]);

  const openDuplicatesModal = () => {
    setDuplicateReasonFilter('all');
    setDuplicateSearch('');
    setDuplicateVisibleCount(DUPLICATE_PAGE_SIZE);
    setShowDuplicatesModal(true);
    fetchDuplicates();
  };

  const filteredDuplicateGroups = useMemo(() => {
    const term = duplicateSearch.trim().toLowerCase();
    return duplicateGroups.filter(group => {
      if (duplicateReasonFilter !== 'all' && !group.reasons.includes(duplicateReasonFilter)) return false;
      if (!term) return true;
      return group.patients.some(p =>
        (p.fullName || '').toLowerCase().includes(term) ||
        String(p.nationalId ?? '').toLowerCase().includes(term) ||
        (p.phoneNumber || '').toLowerCase().includes(term) ||
        (p.serialNumber || '').toLowerCase().includes(term)
      );
    });
  }, [duplicateGroups, duplicateReasonFilter, duplicateSearch]);

  const handleAssignStudent = async (studentId, procedureRequestId = null) => {
    if (!assigningPatient) return;
    setAssignLoading(true);
    setAssignError('');
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };

      // Update patient with new drId - on this exact record, not whichever
      // duplicate shares its national ID.
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/patients/${patientPath(assigningPatient)}`,
        { drId: studentId, patientId: assigningPatient.id, currentDrId: assigningPatient.drId },
        config
      );

      // If this assignment is related to a procedure request, approve it
      if (procedureRequestId) {
        try {
          await axios.put(
            `${process.env.REACT_APP_API_URL}/api/procedure-requests/${procedureRequestId}`,
            {
              status: 'approved',
              responseNotes: `Patient ${assigningPatient.fullName} assigned to student for requested procedure.`
            },
            config
          );
        } catch (procErr) {
          console.error('Error updating procedure request:', procErr);
          // Don't fail the assignment if procedure request update fails
        }
      }

      setShowAssignModal(false);
      setAssigningPatient(null);
      setAssignSearch('');
      setAssignLoading(false);
      setAssignError('');
      setSelectedProcedureType('all');
      setShowProcedureFilter(false);
      setSuccess(procedureRequestId ?
        'Patient assigned successfully and procedure request approved!' :
        'Patient assigned successfully!'
      );

      invalidateAndRefetchPatients();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setAssignError(err.response?.data?.message || 'Failed to assign patient');
      setAssignLoading(false);
    }
  };



  const renderModalContent = () => {
    if (!selectedPerson || !patientDetails) return null;
    
    return (
      <div className="space-y-6">
        <div className={`bg-blue-50 p-5 rounded-xl shadow-sm border border-blue-100`}>
          <h4 className={`text-lg font-semibold text-[${websiteColorPalette.primary}] mb-4 flex items-center`}>
            <FaUserAlt className={`h-5 w-5 mr-2 text-[${websiteColorPalette.primary}]`} />
            Personal Information
          </h4>
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium text-gray-500">Full Name</h4>
                <p className="text-sm text-gray-900 mt-1 font-medium">{patientDetails.fullName}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">National ID</h4>
                <p className="text-sm text-gray-900 mt-1">{patientDetails.nationalId}</p>
              </div>
              {patientDetails.serialNumber && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Serial Number</h4>
                  <p className="text-sm text-gray-900 mt-1">{patientDetails.serialNumber}</p>
                </div>
              )}
              <div>
                <h4 className="text-sm font-medium text-gray-500">Gender</h4>
                <p className="text-sm text-gray-900 mt-1">{patientDetails.gender}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Age</h4>
                <p className="text-sm text-gray-900 mt-1">{patientDetails.age}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Phone Number</h4>
                <p className="text-sm text-gray-900 mt-1">{patientDetails.phoneNumber}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Address</h4>
                <p className="text-sm text-gray-900 mt-1">{patientDetails.address}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Occupation</h4>
                <p className="text-sm text-gray-900 mt-1">{patientDetails.occupation}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Assigned Student</h4>
                <p className="text-sm text-gray-900 mt-1 font-medium">
                  {patientDetails.drId === 'unassigned' || !patientDetails.drId || patientDetails.drId === 'N/A'
                    ? 'Unassigned'
                                              : (patientDetails.studentName || (Array.isArray(students) ? students.find((s) => s.studentId === patientDetails.drId)?.name : null) || 'Unknown Student')
                  }
                </p>
              </div>
              {patientDetails.notes && (
                <div className="sm:col-span-2">
                  <h4 className="text-sm font-medium text-gray-500">Notes</h4>
                  <p className="text-sm text-gray-900 mt-1">{patientDetails.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Medical Information */}
        <div className={`bg-green-50 p-5 rounded-xl shadow-sm border border-green-100`}>
          <h4 className={`text-lg font-semibold text-[${websiteColorPalette.accent}] mb-4 flex items-center`}>
            <FaNotesMedical className={`h-5 w-5 mr-2 text-[${websiteColorPalette.accent}]`} />
            Medical Information
          </h4>
          <div className="bg-white p-4 rounded-lg shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium text-gray-500">Chief Complaint</h4>
                <p className="text-sm text-gray-900 mt-1">{patientDetails.medicalInfo?.chiefComplaint || 'None'}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Current Medications</h4>
                <p className="text-sm text-gray-900 mt-1">{patientDetails.medicalInfo?.currentMedications || 'None'}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Recent Surgical Procedures</h4>
                <p className="text-sm text-gray-900 mt-1">{patientDetails.medicalInfo?.recentSurgicalProcedures || 'None'}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Chronic Diseases</h4>
                <p className="text-sm text-gray-900 mt-1">{(() => {
                  const list = patientDetails.medicalInfo?.chronicDiseases || [];
                  if (!list.length) return 'None';
                  const hasOther = list.includes('Other');
                  const otherNote = patientDetails.medicalInfo?.otherChronicDisease;
                  const base = list.filter(d => d !== 'Other').join(', ');
                  if (hasOther && otherNote) return base ? `${base}; Other: ${otherNote}` : `Other: ${otherNote}`;
                  return base || 'Other';
                })()}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Appointments */}
        {patientDetails.appointments && patientDetails.appointments.length > 0 && (
          <div className={`bg-purple-50 p-5 rounded-xl shadow-sm border border-purple-100`}>
            <h4 className={`text-lg font-semibold text-purple-600 mb-4 flex items-center`}>
              <FaCalendarAlt className="h-5 w-5 mr-2 text-purple-600" />
              Appointments
            </h4>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="space-y-3">
                {patientDetails.appointments.map((appointment, index) => (
                  <div key={index} className="border-l-4 border-purple-400 pl-4 py-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {(() => {
                            try {
                              let date;
                              if (appointment.date) {
                                if (typeof appointment.date === 'string' || typeof appointment.date === 'number') {
                                  date = new Date(appointment.date);
                                } else if (appointment.date.seconds || appointment.date._seconds) {
                                  const secs = appointment.date.seconds || appointment.date._seconds;
                                  date = new Date(secs * 1000);
                                } else if (appointment.date.toDate) {
                                  date = appointment.date.toDate();
                                } else {
                                  date = new Date(appointment.date);
                                }
                                
                                if (isNaN(date.getTime())) {
                                  return 'Invalid date';
                                }
                                
                                return formatNumericDateEG(date) + (appointment.time ? ` at ${appointment.time}` : '');
                              }
                              return 'No date';
                            } catch (error) {
                              return 'Invalid date';
                            }
                          })()}
                        </p>
                        <p className="text-sm text-gray-600">{appointment.reason || 'General consultation'}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        appointment.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                        appointment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {appointment.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) return <Loader />;

  return (
    <div className="flex h-screen bg-gray-50">
      <AssistantSidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gradient-to-br from-blue-50 to-white">
          <div className="max-w-7xl mx-auto">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm"
              >
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-red-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <p className="text-red-700 font-medium">{error}</p>
                </div>
              </motion.div>
            )}

            {success && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-lg shadow-sm"
              >
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-green-500 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <p className="text-green-700 font-medium">{success}</p>
                </div>
              </motion.div>
            )}

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
              <div className="mb-6 sm:mb-8">
                <h1 className={`text-2xl sm:text-3xl md:text-4xl font-bold text-[${websiteColorPalette.primary}] mb-1`}>Patients Management</h1>
                <p className={`text-[${websiteColorPalette.text}]`}>Manage patient records and assignments</p>
              </div>

              <motion.div
                variants={container}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                className="space-y-6 sm:space-y-8"
              >
                <motion.div
                  variants={item}
                  className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 overflow-hidden"
                >
                  {/* Header Section */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
                    <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4">
                      <div className="flex flex-col gap-4">
                        {/* Title and Stats */}
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                            <FaUsers className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate">All Patients</h2>
                            <div className="flex flex-wrap items-center gap-1 sm:gap-2 mt-1">
                              <span className="text-xs sm:text-sm text-gray-600">Total:</span>
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs sm:text-sm font-semibold rounded-full">
                                {pagination.total}
                              </span>
                              {searchTerm && (
                                <>
                                  <span className="text-xs sm:text-sm text-gray-600 hidden sm:inline">•</span>
                                  <span className="text-xs sm:text-sm text-gray-600">Results:</span>
                                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs sm:text-sm font-semibold rounded-full">
                                    {paginatedPatients.length}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
                          <button
                            onClick={handleAddPatient}
                            className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 text-xs sm:text-sm font-medium"
                          >
                            <FaPlus className="h-3 w-3 sm:h-4 sm:w-4" />
                            <span>Add Patient</span>
                          </button>
                          <button
                            onClick={downloadCSV}
                            className="px-3 sm:px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center justify-center gap-2 text-xs sm:text-sm font-medium"
                          >
                            <FaDownload className="h-3 w-3 sm:h-4 sm:w-4" />
                            <span className="hidden sm:inline">Download as Excel</span>
                            <span className="sm:hidden">Excel</span>
                          </button>
                          <button
                            onClick={openDuplicatesModal}
                            className="px-3 sm:px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors flex items-center justify-center gap-2 text-xs sm:text-sm font-medium"
                          >
                            <FaClone className="h-3 w-3 sm:h-4 sm:w-4" />
                            <span>Show Duplicates</span>
                          </button>
                          <button
                            onClick={() => invalidateAndRefetchPatients()}
                            disabled={refreshing}
                            className="px-3 sm:px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 text-xs sm:text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <svg className={`h-3 w-3 sm:h-4 sm:w-4 ${refreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            <span className="hidden sm:inline">{refreshing ? 'Refreshing...' : 'Refresh'}</span>
                            <span className="sm:hidden">{refreshing ? '...' : 'Refresh'}</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Search and Filter Section */}
                  <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 bg-gray-50 border-b border-gray-200">
                    <div className="space-y-3">
                      {/* Search Bar */}
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Search patients..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm transition-all duration-200"
                        />
                        <svg className="absolute left-3 top-3 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        {searchLoading && (
                          <div className="absolute right-10 top-3">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                          </div>
                        )}
                        {searchTerm && (
                          <button
                            onClick={() => setSearchTerm('')}
                            className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>

                      {/* Filters - Mobile Optimized */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                        {/* Sort By */}
                        <div className="relative">
                          <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                          >
                            <option value="registrationDate">Registration Date</option>
                            <option value="fullName">Name</option>
                            <option value="serialNumber">Serial Number</option>
                          </select>
                        </div>

                        {/* Sort Order Toggle */}
                        <button
                          onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white flex items-center justify-center gap-2 transition-all duration-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 text-blue-600 font-medium"
                        >
                          {sortOrder === 'desc' ? (
                            <>
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                              </svg>
                              <span>{sortBy === 'fullName' ? 'Z-A' : sortBy === 'serialNumber' ? 'High-Low' : 'Newest'}</span>
                            </>
                          ) : (
                            <>
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                              <span>{sortBy === 'fullName' ? 'A-Z' : sortBy === 'serialNumber' ? 'Low-High' : 'Oldest'}</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Results Summary */}
                  <div className="px-3 sm:px-4 lg:px-6 py-2 sm:py-3 bg-white border-b border-gray-200">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="text-xs sm:text-sm text-gray-600">
                        Showing <span className="font-semibold text-gray-900">{paginatedPatients.length}</span> of <span className="font-semibold text-gray-900">{pagination.total || patients.length}</span> patients
                      </div>
                      {(searchTerm || studentFilter !== 'all' || sortBy !== 'registrationDate' || sortOrder !== 'desc') && (
                        <button
                          onClick={() => {
                            setSearchTerm('');
                            setStudentFilter('all');
                            setSortBy('registrationDate');
                            setSortOrder('desc');
                          }}
                          className="text-xs text-blue-600 hover:text-blue-800 underline self-start sm:self-auto"
                        >
                          Clear all filters
                        </button>
                      )}
                    </div>
                  </div>




                </motion.div>
                </motion.div>
                      
                {/* Table */}
                <motion.div
                  variants={item}
                  className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 overflow-hidden"
                >
                  <div className="overflow-x-auto rounded-lg border border-gray-200">
                        <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                            <tr>
                              <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                <div className="flex items-center gap-1 sm:gap-2 cursor-pointer hover:text-gray-700 transition-colors"
                                     onClick={() => {
                                       if (sortBy === 'fullName') {
                                         setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
                                       } else {
                                         setSortBy('fullName');
                                         setSortOrder('asc');
                                       }
                                     }}>
                                  <FaUserAlt className="h-3 w-3 sm:h-4 sm:w-4" />
                                  <span className="hidden sm:inline">Patient Name</span>
                                  <span className="sm:hidden">Name</span>
                                  {sortBy === 'fullName' && (
                                    <span className="ml-1 text-xs bg-blue-100 text-blue-600 px-1 rounded">
                                      {sortOrder === 'desc' ? '↓' : '↑'}
                                    </span>
                                  )}
                                </div>
                              </th>
                              <th className="hidden md:table-cell px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Serial Number
                              </th>
                              <th className="hidden lg:table-cell px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                <FaCalendarAlt className="h-3 w-3 sm:h-4 sm:w-4 inline mr-1 sm:mr-2" />
                                Registration Date
                              </th>
                              <th className="hidden sm:table-cell px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned Student</th>
                              <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32 sm:w-48">Actions</th>
                            </tr>
                          </thead>
                        <tbody className="bg-white divide-y divide-gray-200" key={`${searchTerm}-${studentFilter}-${sortBy}-${sortOrder}-${pagination.page}`}>
                          {searchLoading ? (
                            <tr>
                              <td colSpan="5" className="px-3 sm:px-6 py-8 text-center">
                                <div className="flex flex-col items-center justify-center">
                                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
                                  <p className="text-sm text-gray-500">Searching...</p>
                                </div>
                              </td>
                            </tr>
                          ) : !Array.isArray(paginatedPatients) || paginatedPatients.length === 0 ? (
                            <tr>
                              <td colSpan="5" className="px-3 sm:px-6 py-12 text-center">
                                <div className="flex flex-col items-center justify-center space-y-3">
                                  <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
                                    <FaUsers className="h-8 w-8 text-gray-400" />
                                  </div>
                                  <div>
                                    <h3 className="text-lg font-medium text-gray-900">No patients found</h3>
                                    <p className="text-sm text-gray-500">
                                      {searchTerm || studentFilter !== 'all' 
                                        ? 'Try adjusting your search or filters'
                                        : 'No patients have been added yet'
                                      }
                                    </p>
                                  </div>
                                  {searchTerm || studentFilter !== 'all' ? (
                                    <button
                                      onClick={() => {
                                        setSearchTerm('');
                                        setStudentFilter('all');
                                        setSortBy('registrationDate');
                                        setSortOrder('desc');
                                      }}
                                      className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                                    >
                                      Clear all filters
                                    </button>
                                  ) : (
                                    <button
                                      onClick={handleAddPatient}
                                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                                    >
                                      Add your first patient
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                                                      ) : (
                              Array.isArray(paginatedPatients) ? paginatedPatients.map((person, index) => (
                              <motion.tr
                                key={`${person._id}-${index}`}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.2, delay: index * 0.05 }}
                                className="hover:bg-gray-50 transition-all duration-200"
                              >
                                <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm font-semibold text-gray-900 cursor-pointer hover:text-blue-600 transition-colors"
                                    onClick={() => setSelectedPerson(person)}
                                >
                                  <div className="flex items-center">
                                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center mr-2 sm:mr-3 bg-blue-50 flex-shrink-0">
                                      <span className="font-bold text-xs sm:text-sm text-blue-600">
                                        {person.fullName.charAt(0).toUpperCase()}
                                      </span>
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <div className="font-semibold text-sm sm:text-base truncate">{person.fullName}</div>
                                      <div className="text-xs text-gray-500 truncate">
                                        {person.phoneNumber || 'No phone'}
                                      </div>
                                      {/* Mobile-only additional info */}
                                      <div className="sm:hidden text-xs text-gray-400 mt-1">
                                        <div className="truncate">
                                          ID: {person.nationalId}
                                        </div>
                                        {person.serialNumber && (
                                          <div className="truncate">
                                            Serial: {person.serialNumber}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="hidden md:table-cell px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-600 cursor-pointer hover:text-blue-600 transition-colors" onClick={() => setSelectedPerson(person)}>
                                  <code className="px-2 sm:px-3 py-1 rounded-lg text-xs font-mono font-medium bg-gray-100 text-gray-700">
                                    {person.serialNumber || 'N/A'}
                                  </code>
                                </td>
                                <td className="hidden lg:table-cell px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-600 cursor-pointer hover:text-blue-600 transition-colors" onClick={() => setSelectedPerson(person)}>
                                  <div className="flex items-center">
                                    <svg className="h-4 w-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
                                    </svg>
                                    <span className="font-medium">
                                      {/* Day-first (Egyptian) date. formatDateEG already handles
                                          strings, Firestore Timestamps and the serialised
                                          _seconds shape, so this no longer needs its own parser. */}
                                      {formatDateEG(person.registrationDate || person.createdAt, { weekday: undefined }) === 'Invalid Date'
                                        ? 'N/A'
                                        : formatDateEG(person.registrationDate || person.createdAt, { weekday: undefined })}
                                    </span>
                                  </div>
                                </td>
                                <td className="hidden sm:table-cell px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-600">
                                  <div className="flex items-center justify-between">
                                    <span className="cursor-pointer hover:text-blue-600 transition-colors truncate" onClick={() => setSelectedPerson(person)}>
                                      {(() => {
                                        const studentName = person.studentName || (Array.isArray(students) ? students.find((s) => s.studentId === person.drId)?.name : null);
                                        if (!studentName || person.drId === 'N/A' || person.drId === 'unassigned' || !person.drId) {
                                          return <span className="text-orange-600 font-medium">❌ Unassigned</span>;
                                        }
                                        // Show only first and second name
                                        const nameParts = studentName.split(' ');
                                        const displayName = nameParts.length >= 2 
                                          ? `${nameParts[0]} ${nameParts[1]}` 
                                          : studentName;
                                        return displayName;
                                      })()}
                                    </span>
                                  </div>
                                </td>
                                <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-600">
                                  <div className="flex items-center gap-1 sm:gap-2 justify-start">
                                    {/* Assign button */}
                                    <button
                                      className={`p-1 sm:p-1.5 sm:p-2 rounded-lg text-xs font-medium transition-colors ${
                                        (person.drId === 'N/A' || person.drId === 'unassigned' || !person.drId || !(Array.isArray(students) && students.find(s => s.studentId === person.drId)))
                                          ? 'bg-teal-500 text-white hover:bg-teal-600'
                                          : 'bg-gray-300 text-gray-600 cursor-not-allowed opacity-50'
                                      }`}
                                      onClick={e => { 
                                        e.stopPropagation(); 
                                        if (person.drId === 'N/A' || person.drId === 'unassigned' || !person.drId || !(Array.isArray(students) && students.find(s => s.studentId === person.drId))) {
                                          openAssignModal(person);
                                        }
                                      }}
                                      title={person.drId === 'N/A' || person.drId === 'unassigned' || !person.drId || !(Array.isArray(students) && students.find(s => s.studentId === person.drId)) ? "Assign to Student" : "Already Assigned"}
                                    >
                                      <FaUserPlus className="h-3 w-3 sm:h-4 sm:w-4" />
                                    </button>
                                    
                                    {/* Appointment button */}
                                    <button
                                      className="p-1 sm:p-1.5 sm:p-2 bg-blue-500 text-white rounded-lg text-xs font-medium hover:bg-blue-600 transition-colors"
                                      onClick={e => {
                                        e.stopPropagation();
                                        setAppointmentPatient(person);
                                        setAppointmentDefaultStudentId(person.drId && person.drId !== 'N/A' && person.drId !== 'unassigned' ? person.drId : null);
                                        setShowAddAppointmentModal(true);
                                      }}
                                      title="Schedule Appointment"
                                    >
                                      <FaCalendarAlt className="h-3 w-3 sm:h-4 sm:w-4" />
                                    </button>
                                    
                                    {/* Edit button */}
                                    <button
                                      className="p-1 sm:p-1.5 sm:p-2 bg-green-500 text-white rounded-lg text-xs font-medium hover:bg-green-600 transition-colors"
                                      onClick={e => { e.stopPropagation(); handleEditPatient(person); }}
                                      title="Edit Patient"
                                    >
                                      <FaEdit className="h-3 w-3 sm:h-4 sm:w-4" />
                                    </button>
                                    
                                    {/* Delete button */}
                                    <button
                                      className="p-1 sm:p-1.5 sm:p-2 bg-red-500 text-white rounded-lg text-xs font-medium hover:bg-red-600 transition-colors"
                                      onClick={e => { e.stopPropagation(); handleDeletePatient(person); }}
                                      title="Delete Patient"
                                    >
                                      <FaTrash className="h-3 w-3 sm:h-4 sm:w-4" />
                                    </button>
                                  </div>
                                </td>
                                                              </motion.tr>
                              )) : null
                            )}
                          </tbody>
                      </table>
                    </div>

                    {/* Pagination Controls */}
                    {pagination.totalPages > 1 && (
                      <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 px-3 sm:px-4 lg:px-6 py-3 sm:py-4 bg-gray-50 border-t border-gray-200">
                        <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                          <span className="hidden sm:inline">Show</span>
                          <select
                            value={pagination.limit}
                            onChange={(e) => handleLimitChange(Number(e.target.value))}
                            className="px-2 py-1 border border-gray-300 rounded text-xs sm:text-sm"
                          >
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                          <span className="hidden sm:inline">entries per page</span>
                          <span className="sm:hidden">per page</span>
                        </div>
                        
                        <div className="flex items-center gap-1 sm:gap-2">
                          <button
                            onClick={() => handlePageChange(pagination.page - 1)}
                            disabled={!pagination.hasPrevPage}
                            className="px-2 sm:px-3 py-1 text-xs sm:text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <FaChevronLeft className="h-3 w-3" />
                          </button>
                          
                          <span className="text-xs sm:text-sm text-gray-600 px-2">
                            <span className="hidden sm:inline">Page {pagination.page} of {pagination.totalPages}</span>
                            <span className="sm:hidden">{pagination.page}/{pagination.totalPages}</span>
                          </span>
                          
                          <button
                            onClick={() => handlePageChange(pagination.page + 1)}
                            disabled={!pagination.hasNextPage}
                            className="px-2 sm:px-3 py-1 text-xs sm:text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <FaChevronRight className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    )}
                </motion.div>
                </motion.div>
              </div>
        </main>
      </div>

      {/* Patient Details Modal */}
      {selectedPerson && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Patient Details</h2>
              <button
                onClick={() => {
                  setSelectedPerson(null);
                  setPatientDetails(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 sm:p-6">
              {renderModalContent()}
              {/* Assign button in details modal if not assigned */}
                                      {(selectedPerson.drId === 'N/A' || selectedPerson.drId === 'unassigned' || !selectedPerson.drId || !(Array.isArray(students) && students.find(s => s.studentId === selectedPerson.drId))) && (
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <button
                    className={`px-4 sm:px-6 py-3 bg-[${websiteColorPalette.secondary}] text-white rounded-lg font-medium hover:bg-teal-600 transition-colors flex items-center`}
                    onClick={() => openAssignModal(selectedPerson)}
                  >
                    <FaUserPlus className="h-4 w-4 mr-2" />
                    Assign to Student
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Patient Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Add New Patient</h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleSubmitPatient} className="p-4 sm:p-6 space-y-6">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
              )}
              <h3 className={`text-lg font-semibold text-[${websiteColorPalette.primary}] mb-4 border-b border-gray-200 pb-2`}>
                Patient Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name*</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">National ID</label>
                  <input
                    type="text"
                    name="nationalId"
                    value={formData.nationalId}
                    onChange={handleInputChange}
                    onBlur={handleNationalIdBlur}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter National ID (optional)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Serial Number</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      name="serialNumber"
                      value={formData.serialNumber}
                      onChange={handleInputChange}
                      onBlur={handleSerialBlur}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter Serial Number (optional)"
                    />
                    <button
                      type="button"
                      onClick={handleGenerateSerialNumber}
                      className="px-3 py-2 text-sm bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
                      disabled={serialChecking}
                    >
                      {serialChecking ? '...' : 'Random'}
                    </button>
                  </div>
                  {serialValidationMessage && (
                    <p className={`text-xs mt-1 ${serialConflict ? 'text-red-600' : 'text-green-600'}`}>
                      {serialValidationMessage}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Registration Date</label>
                  <input
                    type="date"
                    name="registrationDate"
                    value={formData.registrationDate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number</label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter phone number (optional)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Gender</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Gender (optional)</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Age</label>
                  <input
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter age (optional)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Address</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter address (optional)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Occupation</label>
                  <input
                    type="text"
                    name="occupation"
                    value={formData.occupation}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter occupation (optional)"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Notes (Optional)</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Add any additional notes about the patient"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Assign to Student</label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                      onClick={() => setShowStudentAssignPopup(true)}
                    >
                      {formData.drId && students.find(s => s.studentId === formData.drId)
                        ? `Assigned: ${students.find(s => s.studentId === formData.drId).name}`
                        : 'Assign to Student'}
                    </button>
                    {formData.drId && (
                      <button
                        type="button"
                        className="text-xs text-red-500 underline"
                        onClick={() => setFormData(prev => ({ ...prev, drId: '' }))}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Medical Information Section */}
              <h3 className="text-lg font-semibold text-[#0077B6] mb-4 border-b border-[rgba(0,119,182,0.2)] pb-2">
                Medical Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Chief Complaint</label>
                  <textarea
                    name="medical-chiefComplaint"
                    value={formData.medicalInfo.chiefComplaint}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Describe the patient's main complaint or reason for visit"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Current Medications</label>
                  <textarea
                    name="medical-currentMedications"
                    value={formData.medicalInfo.currentMedications}
                    onChange={handleInputChange}
                    rows="2"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="List any current medications the patient is taking"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Recent Surgical Procedures</label>
                  <textarea
                    name="medical-recentSurgicalProcedures"
                    value={formData.medicalInfo.recentSurgicalProcedures}
                    onChange={handleInputChange}
                    rows="2"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="List any recent surgical procedures"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Chronic Diseases</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {CHRONIC_DISEASE_OPTIONS.map((disease) => (
                      <label key={disease} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          value={disease}
                          checked={formData.medicalInfo.chronicDiseases.includes(disease)}
                          onChange={handleChronicDiseaseChange}
                          className="rounded border-gray-300 text-[#0077B6] focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">{disease}</span>
                      </label>
                    ))}
                  </div>
                  {formData.medicalInfo.chronicDiseases.includes('Other') && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Please specify</label>
                      <input
                        type="text"
                        name="medical-otherChronicDisease"
                        value={formData.medicalInfo.otherChronicDisease}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Describe other chronic disease"
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addSubmitting}
                  className="px-6 py-2 text-white rounded-lg transition-colors hover:brightness-110 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-w-[140px]"
                  style={{ backgroundColor: websiteColorPalette.primary }}
                >
                  {addSubmitting ? (
                    <>
                      <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Add Patient'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Student Modal */}
      {showAssignModal && assigningPatient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">Assign Patient to Student</h2>
              <button
                onClick={() => setShowAssignModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 sm:p-6">
              <div className="mb-4 space-y-3">
                <input
                  type="text"
                  placeholder="Search student by name or ID..."
                  value={assignSearch}
                  onChange={e => setAssignSearch(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                />

                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setShowProcedureFilter(!showProcedureFilter)}
                    className="flex items-center px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <FaFilter className="h-3 w-3 mr-2" />
                    {showProcedureFilter ? 'Hide' : 'Show'} Procedure Requests
                  </button>

                  {showProcedureFilter && (
                    <select
                      value={selectedProcedureType}
                      onChange={e => setSelectedProcedureType(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    >
                      <option value="all">All Procedures</option>
                      <option value="Operative">Operative</option>
                      <option value="Endodontics">Endodontics</option>
                      <option value="Periodontics">Periodontics</option>
                      <option value="Fixed Prosthodontics">Fixed Prosthodontics</option>
                      <option value="Removable Prosthodontics">Removable Prosthodontics</option>
                      <option value="Oral Surgery">Oral Surgery</option>
                    </select>
                  )}
                </div>
              </div>

              <div className="max-h-64 overflow-y-auto">
                {!Array.isArray(students) || students.length === 0 ? (
                  <div className="text-gray-500">No students found</div>
                ) : (
                  (() => {
                    // Filter students based on search
                    const filteredStudents = Array.isArray(students) ? students.filter(student => {
                      if (!assignSearch.trim()) return true;
                      const search = assignSearch.trim().toLowerCase();
                      return (
                        (student.name && student.name.toLowerCase().includes(search)) ||
                        (student.studentId && student.studentId.toLowerCase().includes(search))
                      );
                    }) : [];

                    // Get procedure requests for filtered students
                    const getStudentProcedureRequests = (studentId) => {
                      return procedureRequests.filter(request =>
                        request.studentId === studentId &&
                        request.status === 'pending' &&
                        (selectedProcedureType === 'all' || request.procedureType === selectedProcedureType)
                      );
                    };

                    return filteredStudents.map(student => {
                      const studentRequests = getStudentProcedureRequests(student.studentId);

                      return (
                        <div key={student._id} className="border-b border-gray-100">
                          {/* Student Info */}
                          <div
                            className="p-3 hover:bg-blue-50 cursor-pointer flex justify-between items-center"
                            onClick={() => handleAssignStudent(student.studentId)}
                          >
                            <div className="flex-1">
                              <span className="font-medium">{student.name}</span>
                              <span className="text-gray-500 ml-2">({student.studentId})</span>
                              {studentRequests.length > 0 && (
                                <span className="ml-2 px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                                  {studentRequests.length} pending request{studentRequests.length > 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                            {assignLoading && <span className="ml-2 text-xs text-blue-500">Assigning...</span>}
                          </div>

                          {/* Show procedure requests if filter is enabled */}
                          {showProcedureFilter && studentRequests.length > 0 && (
                            <div className="pl-6 pr-3 pb-3 bg-gray-50">
                              <div className="text-xs text-gray-600 mb-2 font-medium">Pending Procedure Requests:</div>
                              {studentRequests.map(request => (
                                <div
                                  key={request.id || request._id}
                                  className="mb-2 p-2 bg-white rounded border border-gray-200 hover:bg-blue-50 cursor-pointer"
                                  onClick={() => handleAssignStudent(student.studentId, request.id || request._id)}
                                >
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                      <div className="text-sm font-medium text-blue-700">{request.procedureType}</div>
                                      <div className="text-xs text-gray-600">
                                        Requested: {formatNumericDateEG(request.requestDate)}
                                      </div>
                                      {request.notes && (
                                        <div className="text-xs text-gray-500 mt-1 italic">"{request.notes}"</div>
                                      )}
                                    </div>
                                    <div className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                      Click to assign & approve
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()
                )}
              </div>
              {assignError && <div className="text-red-500 mt-2 text-sm">{assignError}</div>}
            </div>
          </div>
        </div>
      )}

      {/* Student Assign Popup for Add Patient */}
      {showStudentAssignPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">Assign to Student</h2>
              <button
                onClick={() => setShowStudentAssignPopup(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <div className="mb-4 space-y-3">
                <input
                  type="text"
                  placeholder="Search student by name or ID..."
                  value={studentSearch}
                  onChange={e => setStudentSearch(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                />

                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setShowProcedureFilter(!showProcedureFilter)}
                    className="flex items-center px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <FaFilter className="h-3 w-3 mr-2" />
                    {showProcedureFilter ? 'Hide' : 'Show'} Procedure Requests
                  </button>

                  {showProcedureFilter && (
                    <select
                      value={selectedProcedureType}
                      onChange={e => setSelectedProcedureType(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                    >
                      <option value="all">All Procedures</option>
                      <option value="Operative">Operative</option>
                      <option value="Endodontics">Endodontics</option>
                      <option value="Periodontics">Periodontics</option>
                      <option value="Fixed Prosthodontics">Fixed Prosthodontics</option>
                      <option value="Removable Prosthodontics">Removable Prosthodontics</option>
                      <option value="Oral Surgery">Oral Surgery</option>
                    </select>
                  )}
                </div>
              </div>

              <div className="max-h-64 overflow-y-auto">
                {students.length === 0 ? (
                  <div className="text-gray-500">No students found</div>
                ) : (
                  (() => {
                    // Filter students based on search
                    const filteredStudents = Array.isArray(students) ? students.filter(student => {
                      if (!studentSearch.trim()) return true;
                      const search = studentSearch.trim().toLowerCase();
                      return (
                        (student.name && student.name.toLowerCase().includes(search)) ||
                        (student.studentId && student.studentId.toLowerCase().includes(search))
                      );
                    }) : [];

                    // Get procedure requests for filtered students
                    const getStudentProcedureRequests = (studentId) => {
                      return procedureRequests.filter(request =>
                        request.studentId === studentId &&
                        request.status === 'pending' &&
                        (selectedProcedureType === 'all' || request.procedureType === selectedProcedureType)
                      );
                    };

                    return filteredStudents.map(student => {
                      const studentRequests = getStudentProcedureRequests(student.studentId);

                      return (
                        <div key={student._id} className="border-b border-gray-100">
                          {/* Student Info */}
                          <div
                            className="p-3 hover:bg-blue-50 cursor-pointer flex justify-between items-center"
                            onClick={() => {
                              setFormData(prev => ({ ...prev, drId: student.studentId }));
                              setShowStudentAssignPopup(false);
                            }}
                          >
                            <div className="flex-1">
                              <span className="font-medium">{student.name}</span>
                              <span className="text-gray-500 ml-2">({student.studentId})</span>
                              {studentRequests.length > 0 && (
                                <span className="ml-2 px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                                  {studentRequests.length} pending request{studentRequests.length > 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Show procedure requests if filter is enabled */}
                          {showProcedureFilter && studentRequests.length > 0 && (
                            <div className="pl-6 pr-3 pb-3 bg-gray-50">
                              <div className="text-xs text-gray-600 mb-2 font-medium">Pending Procedure Requests:</div>
                              {studentRequests.map(request => (
                                <div
                                  key={request.id || request._id}
                                  className="mb-2 p-2 bg-white rounded border border-gray-200 hover:bg-blue-50 cursor-pointer"
                                  onClick={() => {
                                    setFormData(prev => ({ ...prev, drId: student.studentId }));
                                    setShowStudentAssignPopup(false);
                                  }}
                                >
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                      <div className="text-sm font-medium text-blue-700">{request.procedureType}</div>
                                      <div className="text-xs text-gray-600">
                                        Requested: {formatNumericDateEG(request.requestDate)}
                                      </div>
                                      {request.notes && (
                                        <div className="text-xs text-gray-500 mt-1 italic">"{request.notes}"</div>
                                      )}
                                    </div>
                                    <div className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                      Select for procedure
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Show Duplicates Modal - z-40 so Edit / Delete (z-50) open on top of it */}
      {showDuplicatesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-40">
          <div className="bg-white rounded-xl shadow-lg max-w-5xl w-full max-h-[92vh] flex flex-col">
            <div className="p-4 sm:p-6 border-b border-gray-200 flex justify-between items-start gap-4">
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <FaClone className="h-5 w-5 text-amber-500" />
                  Duplicate Patients
                </h2>
                <p className="text-xs sm:text-sm text-gray-500 mt-1">
                  Records that share a National ID, phone number, name or serial number.
                  The oldest record in each group is marked <span className="font-semibold text-green-700">Original</span>.
                  Nothing is changed until you edit or delete a record.
                </p>
              </div>
              <button
                onClick={() => setShowDuplicatesModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                aria-label="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="px-4 sm:px-6 py-3 border-b border-gray-200 bg-gray-50 space-y-3">
              <div className="flex flex-wrap gap-2">
                {['all', 'nationalId', 'phone', 'name', 'serial'].map(reason => {
                  const count = reason === 'all'
                    ? duplicateSummary?.totalGroups
                    : duplicateSummary?.[reason];
                  const active = duplicateReasonFilter === reason;
                  return (
                    <button
                      key={reason}
                      onClick={() => { setDuplicateReasonFilter(reason); setDuplicateVisibleCount(DUPLICATE_PAGE_SIZE); }}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                        active ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                      }`}
                    >
                      {reason === 'all' ? 'All' : DUPLICATE_REASON_LABELS[reason]}
                      {typeof count === 'number' && <span className="ml-1 opacity-80">({count})</span>}
                    </button>
                  );
                })}
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={duplicateSearch}
                  onChange={e => { setDuplicateSearch(e.target.value); setDuplicateVisibleCount(DUPLICATE_PAGE_SIZE); }}
                  placeholder="Search by name, National ID, phone or serial..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
                />
                <button
                  onClick={fetchDuplicates}
                  disabled={duplicatesLoading}
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm disabled:opacity-50"
                >
                  {duplicatesLoading ? 'Loading...' : 'Refresh'}
                </button>
              </div>
              {duplicateSummary && (
                <p className="text-xs text-gray-500">
                  {filteredDuplicateGroups.length} group{filteredDuplicateGroups.length === 1 ? '' : 's'} shown
                  {' · '}{duplicateSummary.totalDuplicateRecords} possible duplicate record{duplicateSummary.totalDuplicateRecords === 1 ? '' : 's'} in total
                </p>
              )}
            </div>

            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4">
              {duplicatesError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{duplicatesError}</div>
              )}
              {duplicatesLoading && duplicateGroups.length === 0 ? (
                <div className="py-12 text-center text-gray-500 text-sm">Looking for duplicate patients...</div>
              ) : filteredDuplicateGroups.length === 0 ? (
                <div className="py-12 text-center text-gray-500 text-sm">
                  {duplicateGroups.length === 0 ? 'No duplicate patients found.' : 'No groups match this filter.'}
                </div>
              ) : (
                <>
                  {filteredDuplicateGroups.slice(0, duplicateVisibleCount).map(group => (
                    <div key={group.key} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="px-3 sm:px-4 py-2 bg-amber-50 border-b border-amber-100 flex flex-wrap items-center gap-2">
                        {group.reasons.map(reason => (
                          <span key={reason} className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs font-medium rounded-full">
                            {DUPLICATE_REASON_LABELS[reason] || reason}
                          </span>
                        ))}
                        <span className="text-xs text-gray-600 ml-auto">{group.count} records</span>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {group.patients.map(p => {
                          const assigned = p.drId && p.drId !== 'unassigned' && p.drId !== 'N/A';
                          const created = formatDateEG(p.createdAt || p.registrationDate, { weekday: undefined });
                          return (
                            <div key={p.id} className={`px-3 sm:px-4 py-3 flex flex-col md:flex-row md:items-center gap-2 md:gap-4 ${p.isOriginal ? 'bg-green-50/40' : ''}`}>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className={`px-2 py-0.5 text-[11px] font-semibold rounded ${
                                    p.isOriginal ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'
                                  }`}>
                                    {p.isOriginal ? 'Original' : 'Duplicate'}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => setSelectedPerson(p)}
                                    className="font-semibold text-sm text-gray-900 hover:text-blue-600 truncate text-left"
                                  >
                                    {p.fullName || 'Unnamed'}
                                  </button>
                                </div>
                                <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-0.5 text-xs text-gray-600">
                                  <span className="truncate">ID: {p.nationalId != null && p.nationalId !== '' ? String(p.nationalId) : '—'}</span>
                                  <span className="truncate">Phone: {p.phoneNumber || '—'}</span>
                                  <span className="truncate">Serial: {p.serialNumber || '—'}</span>
                                  <span className="truncate">Added: {created === 'Invalid Date' ? '—' : created}</span>
                                  <span className="truncate">Student: {assigned ? (p.studentName || p.drId) : 'Unassigned'}</span>
                                  <span className="truncate">Age: {p.age ?? '—'}</span>
                                  <span className="truncate sm:col-span-2">
                                    Records: {p.appointmentsCount || 0} appt · {p.xraysCount || 0} x-ray · {p.treatmentSheetsCount || 0} sheet
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <button
                                  onClick={() => handleEditPatient(p)}
                                  className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-xs font-medium hover:bg-green-600 transition-colors flex items-center gap-1"
                                >
                                  <FaEdit className="h-3 w-3" /> Edit
                                </button>
                                <button
                                  onClick={() => handleDeletePatient(p)}
                                  className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs font-medium hover:bg-red-600 transition-colors flex items-center gap-1"
                                >
                                  <FaTrash className="h-3 w-3" /> Delete
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  {filteredDuplicateGroups.length > duplicateVisibleCount && (
                    <div className="text-center">
                      <button
                        onClick={() => setDuplicateVisibleCount(c => c + DUPLICATE_PAGE_SIZE)}
                        className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
                      >
                        Show more ({filteredDuplicateGroups.length - duplicateVisibleCount} remaining)
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Patient Modal */}
      {showEditModal && editingPatient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Edit Patient</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleSubmitEdit} className="p-4 sm:p-6 space-y-6">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
              )}
              <h3 className={`text-lg font-semibold text-[${websiteColorPalette.primary}] mb-4 border-b border-gray-200 pb-2`}>
                Patient Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name*</label>
                  <input
                    type="text"
                    name="fullName"
                    value={editFormData.fullName}
                    onChange={handleEditInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">National ID</label>
                  <input
                    type="text"
                    name="nationalId"
                    value={editFormData.nationalId}
                    onChange={handleEditInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                    placeholder="Enter National ID"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Serial Number</label>
                  <input
                    type="text"
                    name="serialNumber"
                    value={editFormData.serialNumber}
                    onChange={handleEditInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                    placeholder="Enter Serial Number (optional)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Registration Date</label>
                  <input
                    type="date"
                    name="registrationDate"
                    value={editFormData.registrationDate}
                    onChange={handleEditInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number</label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={editFormData.phoneNumber}
                    onChange={handleEditInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                    placeholder="Enter phone number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Gender</label>
                  <select
                    name="gender"
                    value={editFormData.gender}
                    onChange={handleEditInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                  >
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Age</label>
                  <input
                    type="number"
                    name="age"
                    value={editFormData.age}
                    onChange={handleEditInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                    placeholder="Enter age"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Address</label>
                  <input
                    type="text"
                    name="address"
                    value={editFormData.address}
                    onChange={handleEditInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                    placeholder="Enter address"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Occupation</label>
                  <input
                    type="text"
                    name="occupation"
                    value={editFormData.occupation}
                    onChange={handleEditInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                    placeholder="Enter occupation"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Notes (Optional)</label>
                  <textarea
                    name="notes"
                    value={editFormData.notes}
                    onChange={handleEditInputChange}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                    placeholder="Add any additional notes about the patient"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Assigned Student</label>
                  <select
                    name="drId"
                    value={editFormData.drId}
                    onChange={handleEditInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                  >
                    <option value="unassigned">Unassigned</option>
                    {students.map(student => (
                      <option key={student.studentId} value={student.studentId}>
                        {student.name} ({student.studentId})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <h3 className="text-lg font-semibold text-[#0077B6] mb-4 border-b border-[rgba(0,119,182,0.2)] pb-2">
                Medical Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Chief Complaint</label>
                  <textarea
                    name="medicalInfo.chiefComplaint"
                    value={editFormData.medicalInfo.chiefComplaint}
                    onChange={handleEditInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                    rows="3"
                    placeholder="Enter chief complaint"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Current Medications</label>
                  <textarea
                    name="medicalInfo.currentMedications"
                    value={editFormData.medicalInfo.currentMedications}
                    onChange={handleEditInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                    rows="3"
                    placeholder="Enter current medications"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Recent Surgical Procedures</label>
                  <textarea
                    name="medicalInfo.recentSurgicalProcedures"
                    value={editFormData.medicalInfo.recentSurgicalProcedures}
                    onChange={handleEditInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                    rows="3"
                    placeholder="Enter recent surgical procedures"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Chronic Diseases</label>
                  <div className="space-y-2">
                    {CHRONIC_DISEASE_OPTIONS.map(disease => (
                      <label key={disease} className="flex items-center">
                        <input
                          type="checkbox"
                          value={disease}
                          checked={editFormData.medicalInfo.chronicDiseases.includes(disease)}
                          onChange={handleEditChronicDiseaseChange}
                          className="mr-2"
                        />
                        <span className="text-sm">{disease}</span>
                      </label>
                    ))}
                  </div>
                  {editFormData.medicalInfo.chronicDiseases.includes('Other') && (
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Please specify</label>
                      <input
                        type="text"
                        name="medicalInfo.otherChronicDisease"
                        value={editFormData.medicalInfo.otherChronicDisease}
                        onChange={handleEditInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#20B2AA] focus:border-[#20B2AA]"
                        placeholder="Describe other chronic disease"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editSubmitting}
                  className="px-6 py-2 bg-[#0077B6] text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 min-w-[140px]"
                >
                  {editSubmitting ? (
                    <>
                      <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Updating...
                    </>
                  ) : (
                    'Update Patient'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddAppointmentModal && appointmentPatient && (
        <AddAppointmentModal
          isOpen={showAddAppointmentModal}
          onClose={() => setShowAddAppointmentModal(false)}
          onSuccess={() => {
            setShowAddAppointmentModal(false);
            setSuccess('Appointment created successfully!');
            setTimeout(() => setSuccess(''), 3000);
            invalidateAndRefetchPatients();
          }}
          patient={appointmentPatient}
          defaultStudentId={appointmentDefaultStudentId}
          universityId={user?.university || user?.affiliation?.id}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && deletingPatient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full">
            <div className="p-4 sm:p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <FaTrash className="h-8 w-8 text-red-500" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-semibold text-gray-900">Confirm Delete</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone.</p>
                </div>
              </div>
              <div className="mb-6">
                <p className="text-gray-700">
                  Are you sure you want to delete patient <span className="font-semibold">{deletingPatient.fullName}</span>?
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  This will permanently remove all patient data including medical records, appointments, and treatment sheets.
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={cancelDeletePatient}
                  disabled={deleteSubmitting}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeletePatient}
                  disabled={deleteSubmitting}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {deleteSubmitting && (
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  )}
                  {deleteSubmitting ? 'Deleting...' : 'Delete Patient'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Success Modal */}
      {showDeleteSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full">
            <div className="p-4 sm:p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                    <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-semibold text-gray-900">Patient Deleted</h3>
                  <p className="text-sm text-gray-500">The patient has been successfully removed.</p>
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={() => setShowDeleteSuccessModal(false)}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default withRoleGuard(['assistant'])(AssistantPatients);
